import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchTopQuestionHistoricalData, HistoricalDataApiException, HistoricalDataNotFoundException } from 'tabs/topQuestions/lib/question_clusters_line_chart/questionClustersLineChartApi'
import { selectActiveBotId } from 'tabs/home/reducers/selectors'
import { selectEndDateFilter, selectStartDateFilter } from 'tabs/topQuestions/lib/question_clustering_status/questionClusteringStatusSelectors'
import { TopQuestionsHistoricalDataNotFound } from 'tabs/topQuestions/components/top_questions_historical_data_not_found/TopQuestionsHistoricalDataNotFound'
import { TopQuestionsHistoricalDataError } from 'tabs/topQuestions/components/top_questions_historical_data_error/TopQuestionsHistoricalDataError'
import { TopQuestionsHistoricalDataLoader } from 'tabs/topQuestions/components/top_questions_historical_data_loader/TopQuestionsHistoricalDataLoader'
import { ClusteringExecutionData, ClusteringStatus } from 'tabs/topQuestions/lib/question_clustering_status/questionClusteringStatusTypes'
import { ClusteringCheckStatus } from 'tabs/topQuestions/lib/question_clustering_status/questionClusteringStatusSlice'
import { updateTopQuestionsColorMap } from 'tabs/topQuestions/lib/top_questions/topQuestionsActions'
import { TopQuestionsLineChart } from 'tabs/topQuestions/components/top_questions_historical_data/TopQuestionsLineChart'

type TopQuestionReqStatus =
  'RESOLVED' |
  'PENDING' |
  'NOT_FOUND' |
  'FAILED' |
  'NONE'

type TopQuestionDataStatus = { status: 'RESOLVED', data: ClusteringExecutionData[] }
  | { status: 'PENDING_FETCH' }
  | { status: 'PENDING_TRAINING' }
  | { status: 'NOT_FOUND' }
  | { status: 'FETCH_FAILED' }
  | { status: 'TRAIN_FAILED' }
  | { status: 'NONE' }

type TopQuestionsLineChartInitializerProps = {
  trainingStatus: ClusteringCheckStatus
  executionId: string | undefined
}

export const TopQuestionsLineChartInitializer: React.FC<TopQuestionsLineChartInitializerProps> = ({
                                                                                                    executionId,
                                                                                                    trainingStatus,
                                                                                                  }) => {
  const dispatch = useDispatch()
  const botId = useSelector(selectActiveBotId)
  const selectedStartDate = useSelector(selectStartDateFilter)
  const selectedEndDate = useSelector(selectEndDateFilter)

  const [fetchDataStatus, setFetchDataStatus] = useState<TopQuestionReqStatus>('NONE')
  const [clusteredData, setClusteredData] = useState<ClusteringExecutionData[] | undefined>(undefined)

  const isNotTraining = trainingStatus.status == 'RESOLVED' || trainingStatus.status == 'NEW'

  // called when there was no data and user manually triggered clustering
  useEffect(() => {
    // if clustering finished successfully
    if (trainingStatus.status === ClusteringStatus.RESOLVED) {
      fetchTopQuestionsHistoricalData()
    }

  }, [trainingStatus.status])

  //triggered when execution id changes due to the change in the selected date or type filter
  useEffect(() => {
    if (executionId != undefined && isNotTraining) {
      fetchTopQuestionsHistoricalData()
    }
    if (executionId == undefined && isNotTraining) {
      setFetchDataStatus('NOT_FOUND')
    }
  }, [executionId])


  function onStartTraining() {
    console.log('Training started')
  }

  function fetchTopQuestionsHistoricalData() {
    console.log('------ FETCHING TRAINING DATA ------')
    setFetchDataStatus('PENDING')
    fetchTopQuestionHistoricalData(botId, executionId)
      .then(resp => {
        dispatch(updateTopQuestionsColorMap(resp.colors))
        setFetchDataStatus('RESOLVED')
        setClusteredData(resp.clustersData)
      })
      .catch(error => {
        if (error instanceof HistoricalDataNotFoundException) {
          setFetchDataStatus('NOT_FOUND')
        } else if (error instanceof HistoricalDataApiException) {
          setFetchDataStatus('FAILED')
        }
      })
  }

  if (isNotTraining) {
    switch (fetchDataStatus) {
      case 'RESOLVED':
        return <TopQuestionsLineChart lineChartData={clusteredData} />
      case 'NOT_FOUND':
        return <TopQuestionsHistoricalDataNotFound onStartTraining={onStartTraining}
                                                   minDate={selectedStartDate}
                                                   maxDate={selectedEndDate} />
      case 'FAILED':
        return <TopQuestionsHistoricalDataError sendFetchRequest={fetchTopQuestionsHistoricalData} />
      case 'PENDING':
        return <TopQuestionsHistoricalDataLoader message={'Loading Data'} />
      case 'NONE':
        return <></>
    }
  } else {
    switch (trainingStatus.status) {
      case 'REJECTED':
        return <p>Failed to train the data, please try again later</p>
      case 'PENDING':
        return <TopQuestionsHistoricalDataLoader message={'Training Data'} />
    }
  }

}
